/**
 * Renders either a country flag emoji (for international chapters)
 * or a US state map image (from /images/states/{state}.png) in a
 * dark-colored rectangle.
 *
 * International chapters use flag emojis for crisp rendering.
 * US states use gold silhouette PNG images.
 */

interface StateIconProps {
  state: string;
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}

// International chapters → flag emoji (renders as actual flag on all devices)
const FLAG_MAP: Record<string, string> = {
  'uk': '🇬🇧',
  'united kingdom': '🇬🇧',
  'england': '🇬🇧',
  'toronto': '🇨🇦',
  'canada': '🇨🇦',
  'ontario': '🇨🇦',
  'south ontario': '🇨🇦',
  'new south wales': '🇦🇺',
  'australia': '🇦🇺',
  // African cities/countries
  'sierra leone': '🇸🇱',
  'freetown': '🇸🇱',
  'friitong': '🇸🇱',
  'lagos': '🇳🇬',
  'nigeria': '🇳🇬',
  'accra': '🇬🇭',
  'ghana': '🇬🇭',
  'banjul': '🇬🇲',
  'gambia': '🇬🇲',
  'senegal': '🇸🇳',
  'dakar': '🇸🇳',
  'south africa': '🇿🇦',
  'johannesburg': '🇿🇦',
  'cape town': '🇿🇦',
  'kenya': '🇰🇪',
  'nairobi': '🇰🇪',
  'ivory coast': '🇨🇮',
  "cote d'ivoire": '🇨🇮',
  'abidjan': '🇨🇮',
  'cameroon': '🇨🇲',
  'douala': '🇨🇲',
  // European
  'germany': '🇩🇪',
  'netherlands': '🇳🇱',
  'belgium': '🇧🇪',
  'france': '🇫🇷',
  'paris': '🇫🇷',
  // US states → US flag
  'georgia': '🇺🇸',
  'california': '🇺🇸',
  'texas': '🇺🇸',
  'new york': '🇺🇸',
  'florida': '🇺🇸',
  'virginia': '🇺🇸',
  'north carolina': '🇺🇸',
  'south carolina': '🇺🇸',
  'maryland': '🇺🇸',
  'washington': '🇺🇸',
  'washington dc': '🇺🇸',
  'illinois': '🇺🇸',
  'ohio': '🇺🇸',
  'michigan': '🇺🇸',
  'pennsylvania': '🇺🇸',
  'new jersey': '🇺🇸',
  'connecticut': '🇺🇸',
  'massachusetts': '🇺🇸',
  'colorado': '🇺🇸',
  'minnesota': '🇺🇸',
  'louisiana': '🇺🇸',
  'tennessee': '🇺🇸',
  'alabama': '🇺🇸',
  'mississippi': '🇺🇸',
  'arizona': '🇺🇸',
  'nevada': '🇺🇸',
  'oregon': '🇺🇸',
};

// Pool of dark background colors for variety
const DARK_COLORS = [
  '#1a2744', // dark navy
  '#2d1b4e', // dark purple
  '#1b3a2a', // dark green
  '#3d1f1f', // dark maroon
  '#1f2d3d', // dark steel
  '#2a1a3d', // dark violet
  '#1d3333', // dark teal
  '#3b2a1a', // dark brown
];

function toFilename(state: string): string {
  return state.toLowerCase().trim().replace(/\s+/g, '-');
}

/** Simple hash to pick a consistent color per state name */
function getColorForState(state: string): string {
  let hash = 0;
  for (let i = 0; i < state.length; i++) {
    hash = state.charCodeAt(i) + ((hash << 5) - hash);
  }
  return DARK_COLORS[Math.abs(hash) % DARK_COLORS.length];
}

export function StateIcon({ state, size = 28, style }: StateIconProps) {
  const normalized = state.toLowerCase().trim();
  const flag = FLAG_MAP[normalized];

  // All chapters now use flag emojis — default to globe for unlisted
  const displayFlag = flag || '🌍';

  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.7,
        lineHeight: 1,
        ...style,
      }}
    >
      {displayFlag}
    </div>
  );
}
